import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { ConnectionForm } from '../../components/DatabaseConnector/ConnectionForm';
import { IntrospectionTable } from '../../components/DatabaseConnector/IntrospectionTable';
import { MultiTableSplitsAndMetricsViewer } from '../../components/DatabaseConnector/MultiTableSplitsAndMetricsViewer';
import { JoinSuggestions } from '../../components/DatabaseConnector/JoinSuggestions';
import { PreviewTable } from '../../components/DatabaseConnector/PreviewTable';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';
import {
  GET_DB_CONNECTOR_CONFIG,
  SAVE_DB_CONNECTOR_CONFIG,
  GET_CONNECTORS,
  GET_DATABASE_METRICS_FOR_CUSTOM_REPORT,
  GET_DATABASE_SPLITS_FOR_CUSTOM_REPORT,
} from 'src/utils/gql/queries';
import { CREATE_DATABASE_CONNECTOR } from 'src/utils/gql/mutations';

const steps = [
  'Connessione',
  'Selezione Tabella',
  'Classificazione Automatica',
  'Join & Custom Report',
  'Anteprima',
];

export const DatabaseConnector: React.FC = () => {
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const client = useApolloClient();
  const { databaseConnectorId } = useConnectors();
  const [activeStep, setActiveStep] = useState(0);
  const [connectionData, setConnectionData] = useState<any>(null);
  const [selectedTables, setSelectedTables] = useState<any[]>([]);
  const [selectedSplits, setSelectedSplits] = useState<string[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [customReportQuery, setCustomReportQuery] = useState<string>('');
  const [connectionId, setConnectionId] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [persistedConnectorId, setPersistedConnectorId] = useState<string>('');

  const [createDatabaseConnector, { loading: savingConnector }] = useMutation(
    CREATE_DATABASE_CONNECTOR,
  );
  const [saveDbConfig, { loading: savingConfig }] = useMutation(
    SAVE_DB_CONNECTOR_CONFIG,
  );

  // Carica config esistente se c'è un connettore DB
  const { data: cfgData } = useQuery(GET_DB_CONNECTOR_CONFIG, {
    variables: { connectorId: databaseConnectorId || '' },
    skip: !databaseConnectorId,
    fetchPolicy: 'network-only',
  });

  // parsing util
  const parsedTablesFromConfig = useMemo(() => {
    const list: string[] =
      cfgData?.getDatabaseConnectorConfig?.selectedTables || [];
    return list.map((s) => {
      const [schema, tableName] = (s || '').split('.');
      return {
        schema: schema || 'public',
        tableName: tableName || s,
        table: tableName || s,
      };
    });
  }, [cfgData]);

  useEffect(() => {
    if (cfgData?.getDatabaseConnectorConfig) {
      setSelectedTables(parsedTablesFromConfig);
      setSelectedSplits(
        cfgData.getDatabaseConnectorConfig.selectedSplits || [],
      );
      setSelectedMetrics(
        cfgData.getDatabaseConnectorConfig.selectedMetrics || [],
      );
      // opzionale: set display name se serve, qui non lo riceviamo
    }
  }, [cfgData, parsedTablesFromConfig]);

  // In modalità edit, salta direttamente allo step "Selezione Tabella"
  useEffect(() => {
    if (databaseConnectorId && cfgData?.getDatabaseConnectorConfig) {
      setActiveStep(1);
    }
  }, [databaseConnectorId, cfgData]);

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleConnectionCreated = async (data: any, id: string) => {
    setConnectionData(data);
    setConnectionId(id);
    setPersistedConnectorId(id);

    if (!displayName && data?.name) {
      setDisplayName((data.name as string).trim());
    }

    try {
      // Aggiorna config/tabelle solo se serve sincronizzare il provider connettori
      if (!databaseConnectorId) {
        await client.refetchQueries({ include: [GET_CONNECTORS] });
      }
    } catch (e) {
      console.error('Failed to refresh connectors after connection save:', e);
    }

    handleNext();
  };

  const handleTablesSelected = async (tables: any[]) => {
    setSelectedTables(tables);

    const connectorIdToUse =
      persistedConnectorId || connectionId || databaseConnectorId;
    if (connectorIdToUse && tables.length > 0) {
      try {
        const tablesAsStrings = tables.map(
          (t) => `${t.schema}.${t.tableName || t.table}`,
        );
        await saveDbConfig({
          variables: {
            input: {
              connectorId: connectorIdToUse,
              selectedTables: tablesAsStrings,
              selectedSplits,
              selectedMetrics,
            },
          },
        });
      } catch (error) {
        console.error('Failed to persist selected database tables:', error);
      }
    }

    handleNext();
  };

  const handleFieldsSelected = async (splits: string[], metrics: string[]) => {
    setSelectedSplits(splits);
    setSelectedMetrics(metrics);

    const connectorIdToUse =
      persistedConnectorId || connectionId || databaseConnectorId;
    if (connectorIdToUse && selectedTables.length > 0) {
      try {
        const tablesAsStrings = selectedTables.map(
          (t) => `${t.schema}.${t.tableName || t.table}`,
        );
        await saveDbConfig({
          variables: {
            input: {
              connectorId: connectorIdToUse,
              selectedTables: tablesAsStrings,
              selectedSplits: splits,
              selectedMetrics: metrics,
            },
          },
        });
        await client.refetchQueries({
          include: [
            GET_DATABASE_METRICS_FOR_CUSTOM_REPORT,
            GET_DATABASE_SPLITS_FOR_CUSTOM_REPORT,
            GET_DB_CONNECTOR_CONFIG,
          ],
        });
      } catch (error) {
        console.error('Failed to persist database connector fields:', error);
      }
    }

    handleNext();
  };

  const handleSplitsChange = (splits: string[]) => setSelectedSplits(splits);
  const handleMetricsChange = (metrics: string[]) =>
    setSelectedMetrics(metrics);
  const handleGenerateReport = (query: string) => setCustomReportQuery(query);

  const handleSaveConnector = async () => {
    try {
      // Usa prima l'ID persistito in questo wizard, altrimenti quello da provider
      let connectorIdToUse = persistedConnectorId || databaseConnectorId;

      // Se non abbiamo ancora un connector persistito E non esiste già un connettore DB, creiamolo ora
      if (!connectorIdToUse) {
        const payload = {
          displayName:
            (connectionData?.name && String(connectionData.name).trim()) ||
            (displayName && displayName.trim()) ||
            `Database ${new Date().toLocaleDateString()}`,
          connectionData: JSON.stringify(connectionData || {}),
          selectedTables: JSON.stringify(selectedTables || []),
          selectedSplits: JSON.stringify(selectedSplits || []),
          selectedMetrics: JSON.stringify(selectedMetrics || []),
        } as any;
        const res = await createDatabaseConnector({ variables: payload });
        connectorIdToUse =
          res?.data?.createDatabaseConnector || connectorIdToUse;
        if (connectorIdToUse) setPersistedConnectorId(connectorIdToUse);
      }

      if (connectorIdToUse) {
        // Update config esistente
        const tablesAsStrings = selectedTables.map(
          (t) => `${t.schema}.${t.tableName || t.table}`,
        );
        await saveDbConfig({
          variables: {
            input: {
              connectorId: connectorIdToUse,
              selectedTables: tablesAsStrings,
              selectedSplits,
              selectedMetrics,
            },
          },
        });

        // Update Apollo cache to force connector refetch
        await client.refetchQueries({
          include: [
            GET_CONNECTORS,
            GET_DATABASE_METRICS_FOR_CUSTOM_REPORT,
            GET_DATABASE_SPLITS_FOR_CUSTOM_REPORT,
            GET_DB_CONNECTOR_CONFIG,
          ],
        });

        navigate('/connectors');
        return;
      }

      // Fallback per compatibilità (non dovrebbe mai arrivare qui)
      // TODO: gestire logging in modo appropriato in produzione
    } catch (error) {
      // TODO: gestire logging in modo appropriato in produzione
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <ConnectionForm onConnectionCreated={handleConnectionCreated} />;
      case 1:
        if (!connectionId && !databaseConnectorId) {
          return (
            <Box>
              <Typography color="error" sx={{ mb: 2 }}>
                Crea una connessione prima di procedere alla selezione delle
                tabelle.
              </Typography>
              <ConnectionForm onConnectionCreated={handleConnectionCreated} />
            </Box>
          );
        }
        return (
          <IntrospectionTable
            connectionId={connectionId || databaseConnectorId!}
            onTablesSelected={handleTablesSelected}
            selectedTables={selectedTables}
          />
        );
      case 2:
        return (
          <MultiTableSplitsAndMetricsViewer
            connectionId={connectionId || databaseConnectorId!}
            tables={selectedTables}
            initialSplits={selectedSplits}
            initialMetrics={selectedMetrics}
            onFieldsSelected={handleFieldsSelected}
          />
        );
      case 3:
        return (
          <JoinSuggestions
            connectionId={connectionId || databaseConnectorId!}
            mainTable={selectedTables[0]?.tableName}
            schema={selectedTables[0]?.schema}
            selectedSplits={selectedSplits}
            selectedMetrics={selectedMetrics}
            onGenerateReport={handleGenerateReport}
            onSplitsChange={handleSplitsChange}
            onMetricsChange={handleMetricsChange}
            onSaveAndContinue={handleSaveConnector}
          />
        );
      case 4:
        return (
          <PreviewTable
            connectionId={connectionId || databaseConnectorId!}
            schema={selectedTables[0]?.schema}
            table={selectedTables[0]?.tableName}
            fields={[...selectedSplits, ...selectedMetrics]}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        padding: '50px 40px 40px 280px',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        '@media (max-width: 1200px)': { padding: '50px 30px 40px 270px' },
        '@media (max-width: 1024px)': {
          padding: '50px 30px 40px 270px',
          maxWidth: '100%',
        },
        '@media (max-width: 900px)': {
          padding: '50px 30px 40px 250px',
          maxWidth: '100%',
        },
        '@media (max-width: 800px)': { padding: '40px 20px 20px 240px' },
        '@media (max-width: 600px)': {
          padding: '80px 20px 20px 20px',
          maxWidth: '100%',
        },
        '@media (max-width: 480px)': { padding: '60px 16px 16px 16px' },
      }}
    >
      <Typography variant="h4" gutterBottom>
        Database Connector
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Paper sx={{ p: 3, overflow: 'visible' }}>
        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Indietro
          </Button>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                await handleSaveConnector();
                showMessage('Saved successfully', 'success');
              }}
              disabled={savingConnector || savingConfig}
            >
              {savingConfig ? 'Saving…' : 'Save changes'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
